import React, { useCallback, useRef } from 'react';
import { CELL, SWIPE_PX, GEM_META } from '../constants';
import { GemArt, GemDefs } from './GemArt';
import { isAdjacent } from '../boardModel';
import i18n from '../../../../i18n';

const center = (x, y) => [x * CELL + CELL / 2, y * CELL + CELL / 2];

/** Deterministic zig-zag from (x1,y1) to (x2,y2) for rainbow lightning. */
function zigzag(x1, y1, x2, y2, seed) {
  const pts = [[x1, y1]];
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;
  for (let i = 1; i < 4; i += 1) {
    const t = i / 4;
    const off = (((seed * 7 + i * 13) % 5) - 2) * 9;
    pts.push([x1 + dx * t + nx * off, y1 + dy * t + ny * off]);
  }
  pts.push([x2, y2]);
  return pts.map((p) => p.map((v) => v.toFixed(1)).join(',')).join(' ');
}

function SpecialEffect({ fx, width, height }) {
  const W = width * CELL;
  const H = height * CELL;
  const [ax, ay] = fx.at;
  const [cx, cy] = center(ax, ay);
  const rows = [];
  const cols = [];
  switch (fx.special) {
    case 'LINE_H': rows.push(ay); break;
    case 'LINE_V': cols.push(ax); break;
    case 'CROSS': rows.push(ay); cols.push(ax); break;
    case 'BIG_CROSS':
      [-1, 0, 1].forEach((d) => {
        if (ay + d >= 0 && ay + d < height) rows.push(ay + d);
        if (ax + d >= 0 && ax + d < width) cols.push(ax + d);
      });
      break;
    default: break;
  }
  const origin = { transformOrigin: `${cx}px ${cy}px` };
  if (rows.length || cols.length) {
    return (
      <g className="m3-fx m3-fx--laser" data-testid={`fx-${fx.special}`}>
        {rows.map((r) => (
          <g key={`r${r}`} className="m3-laser m3-laser--h" style={origin}>
            <rect x="0" y={r * CELL + 22} width={W} height="56" rx="28" fill="#7fe7ff" opacity="0.35" />
            <rect x="0" y={r * CELL + 42} width={W} height="16" rx="8" fill="#ffffff" />
          </g>
        ))}
        {cols.map((c) => (
          <g key={`c${c}`} className="m3-laser m3-laser--v" style={origin}>
            <rect x={c * CELL + 22} y="0" width="56" height={H} rx="28" fill="#7fe7ff" opacity="0.35" />
            <rect x={c * CELL + 42} y="0" width="16" height={H} rx="8" fill="#ffffff" />
          </g>
        ))}
      </g>
    );
  }
  if (fx.special === 'BOMB' || fx.special === 'BIG_BOMB') {
    const r = fx.special === 'BIG_BOMB' ? 260 : 165;
    return (
      <g className="m3-fx m3-fx--blast" data-testid={`fx-${fx.special}`} transform={`translate(${cx} ${cy})`}>
        <circle className="m3-blast" r={r} fill="url(#m3-blast-grad)" />
        <circle className="m3-blast m3-blast--ring" r={r * 0.9} fill="none" stroke="#fff3c4" strokeWidth="10" />
      </g>
    );
  }
  // RAINBOW / RAINBOW_RAINBOW: lightning from the orb to every target
  const color = (GEM_META[fx.gem] || {}).light || '#ffffff';
  return (
    <g className="m3-fx m3-fx--bolts" data-testid={`fx-${fx.special}`}>
      <circle className="m3-blast" cx={cx} cy={cy} r="90" fill="url(#m3-orb-glow)" style={origin} />
      {(fx.cells || []).slice(0, 40).map((c, i) => {
        const [tx, ty] = center(c[0], c[1]);
        return (
          <polyline
            key={`${c[0]}-${c[1]}`}
            className="m3-bolt"
            points={zigzag(cx, cy, tx, ty, i + fx.n)}
            fill="none"
            stroke={color}
            strokeWidth="6"
            strokeLinejoin="round"
            style={{ animationDelay: `${(i % 8) * 18}ms` }}
          />
        );
      })}
    </g>
  );
}

function Effect({ fx, width, height }) {
  if (fx.kind === 'special') return <SpecialEffect fx={fx} width={width} height={height} />;
  if (fx.kind === 'spark') {
    const [cx, cy] = center(fx.x, fx.y);
    return (
      <g className="m3-fx m3-spark" transform={`translate(${cx} ${cy})`}>
        {[0, 60, 120, 180, 240, 300].map((a) => (
          <g key={a} transform={`rotate(${a})`}>
            <path className="m3-spark__ray" d="M0 -14 l5 -14 l-5 -14 l-5 14z" fill="#fff8cf" />
          </g>
        ))}
      </g>
    );
  }
  if (fx.kind === 'float') {
    const [cx, cy] = center(fx.x, fx.y);
    const big = (fx.combo || 1) >= 2;
    return (
      <text
        className={`m3-float${big ? ' m3-float--combo' : ''}`}
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="middle"
        data-testid="m3-float"
      >
        {fx.text}
      </text>
    );
  }
  if (fx.kind === 'shuffle') {
    return <rect className="m3-fx m3-shuffle-flash" x="0" y="0" width={width * CELL} height={height * CELL} rx="24" fill="#ffffff" />;
  }
  return null;
}

/**
 * The board: background tiles, pieces (CSS-transitioned <g>s), effects and an input layer.
 * Input: swipe a gem towards a neighbour (pointer events, threshold SWIPE_PX) or tap two adjacent gems.
 */
export default function Match3Board({
  width, height, pieces, effects = [], selected, hint, disabled, onSelect, onSwap,
}) {
  const dragRef = useRef(null);

  const handleDown = useCallback((x, y, e) => {
    if (disabled) return;
    dragRef.current = { x, y, sx: e.clientX || 0, sy: e.clientY || 0, done: false };
    try { e.currentTarget?.ownerSVGElement?.setPointerCapture?.(e.pointerId); } catch (err) { /* ignore */ }
  }, [disabled]);

  const handleMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d || d.done || disabled) return;
    const dx = (e.clientX || 0) - d.sx;
    const dy = (e.clientY || 0) - d.sy;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < SWIPE_PX) return;
    d.done = true;
    const horizontal = Math.abs(dx) >= Math.abs(dy);
    const tx = d.x + (horizontal ? Math.sign(dx) : 0);
    const ty = d.y + (horizontal ? 0 : Math.sign(dy));
    if (tx < 0 || ty < 0 || tx >= width || ty >= height) return;
    onSwap([d.x, d.y], [tx, ty]);
  }, [disabled, width, height, onSwap]);

  const handleUp = useCallback(() => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d || d.done || disabled) return;
    const cell = [d.x, d.y];
    if (selected && isAdjacent(selected, cell)) {
      onSwap(selected, cell);
    } else if (selected && selected[0] === d.x && selected[1] === d.y) {
      onSelect(null);
    } else {
      onSelect(cell);
    }
  }, [disabled, selected, onSelect, onSwap]);

  const W = width * CELL;
  const H = height * CELL;
  const tiles = [];
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      tiles.push(
        <rect
          key={`t${x}-${y}`}
          x={x * CELL + 3}
          y={y * CELL + 3}
          width={CELL - 6}
          height={CELL - 6}
          rx="16"
          className={(x + y) % 2 ? 'm3-tile m3-tile--alt' : 'm3-tile'}
        />,
      );
    }
  }

  const hintCells = Array.isArray(hint) ? hint : [];

  return (
    <svg
      className={`m3-board${disabled ? ' is-disabled' : ''}`}
      viewBox={`-8 -8 ${W + 16} ${H + 16}`}
      preserveAspectRatio="xMidYMid meet"
      data-testid="m3-board"
      onPointerMove={handleMove}
      onPointerUp={handleUp}
      onPointerCancel={() => { dragRef.current = null; }}
      role="grid"
      aria-label={i18n.t('match3.board_aria', 'Match-3 board')}
    >
      <GemDefs />
      <defs>
        <radialGradient id="m3-blast-grad">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="0.35" stopColor="#ffd25e" stopOpacity="0.8" />
          <stop offset="1" stopColor="#ff5a1f" stopOpacity="0" />
        </radialGradient>
        <clipPath id="m3-clip"><rect x="0" y="0" width={W} height={H} rx="24" /></clipPath>
      </defs>
      <rect className="m3-frame" x="-8" y="-8" width={W + 16} height={H + 16} rx="30" />
      <g clipPath="url(#m3-clip)">
        {tiles}
        {selected && (
          <rect className="m3-selected" x={selected[0] * CELL + 3} y={selected[1] * CELL + 3} width={CELL - 6} height={CELL - 6} rx="16" data-testid="m3-selected" />
        )}
        {hintCells.map((c) => (
          <rect key={`h${c[0]}-${c[1]}`} className="m3-hint" x={c[0] * CELL + 6} y={c[1] * CELL + 6} width={CELL - 12} height={CELL - 12} rx="16" data-testid="m3-hint" />
        ))}
        {pieces.map((p) => {
          const cls = ['m3-piece'];
          if (p.pop) cls.push('is-pop');
          if (p.born) cls.push('is-born');
          if (p.shuffled) cls.push('is-shuffled');
          const style = {
            transform: `translate(${p.x * CELL + CELL / 2}px, ${p.y * CELL + CELL / 2}px)`,
            transitionDuration: `${p.dur || 0}ms`,
          };
          const wiggle = p.wiggle
            ? { '--m3-wx': `${p.wiggle.dx * 34}px`, '--m3-wy': `${p.wiggle.dy * 34}px` }
            : undefined;
          return (
            <g
              key={p.id}
              className={cls.join(' ')}
              style={style}
              data-testid={p.pop ? undefined : `piece-${p.x}-${p.y}`}
              data-gem={p.gem}
              data-special={p.special || ''}
              data-wiggle={p.wiggle ? '1' : undefined}
            >
              <g
                className={p.drop ? 'm3-piece__drop' : undefined}
                style={p.drop ? { '--m3-drop': `${-p.drop}px`, animationDuration: `${p.dur || 300}ms` } : undefined}
              >
                <g key={p.wiggle ? `w${p.wiggle.n}` : 'still'} className={p.wiggle ? 'm3-piece__fx is-wiggle' : 'm3-piece__fx'} style={wiggle}>
                  <GemArt gem={p.gem} special={p.special} />
                </g>
              </g>
            </g>
          );
        })}
        {effects.map((fx) => <Effect key={fx.id} fx={fx} width={width} height={height} />)}
      </g>
      <g className="m3-input">
        {Array.from({ length: height }).map((_, y) => Array.from({ length: width }).map((__, x) => (
          <rect
            key={`c${x}-${y}`}
            x={x * CELL}
            y={y * CELL}
            width={CELL}
            height={CELL}
            fill="transparent"
            data-testid={`cell-${x}-${y}`}
            onPointerDown={(e) => handleDown(x, y, e)}
          />
        )))}
      </g>
    </svg>
  );
}
